import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cake, Loader2, PartyPopper } from 'lucide-react';
import { fetchBirthday, claimBirthday, replayBirthdayCelebration } from '@/lib/api';
import { useBirthdayCelebration } from '@/store/birthdayCelebration';
import styles from './BirthdayRewardCard.module.css';

/**
 * Birthday reward card. Three states, driven by the shared ['birthday'] query:
 *   - claim_eligible          → the Claim button; claiming fires the celebration
 *                               overlay and the ledger award (once/year, atomic).
 *   - withheld (anti-abuse)   → a friendly "unlocks once you use EV Initiative".
 *   - celebration.window_open → a Replay affordance (post-claim, capped 3 / 7d).
 * Otherwise it renders nothing.
 */
export function BirthdayRewardCard() {
  const { t } = useTranslation('rewards');
  const qc = useQueryClient();
  const [withheld, setWithheld] = useState(false);
  const present = useBirthdayCelebration((s) => s.present);

  const birthdayQ = useQuery({
    queryKey: ['birthday'],
    queryFn: fetchBirthday,
    staleTime: 60_000,
  });
  const data = birthdayQ.data;

  const claim = useMutation({
    mutationFn: claimBirthday,
    onSuccess: (res) => {
      if (res.withheld) {
        setWithheld(true); // anti-abuse — friendly, not an error; nothing stamped
      } else if (res.claimed) {
        present({ points: res.points_awarded }); // fire the celebration overlay
      }
      qc.invalidateQueries({ queryKey: ['birthday'] });
      qc.invalidateQueries({ queryKey: ['gamification', 'user-summary'] });
    },
  });

  const replay = useMutation({
    mutationFn: replayBirthdayCelebration,
    onSuccess: (res) => {
      if (res.granted) {
        present({ points: data?.bonus_points ?? 0, isLast: res.is_last });
      }
      qc.invalidateQueries({ queryKey: ['birthday'] });
    },
  });

  // Replay affordance — takes precedence once the driver has claimed and is
  // still inside the 7-day window with replays remaining.
  if (data?.celebration.window_open) {
    return (
      <div className={styles.card}>
        <div className={styles.row}>
          <div className={styles.iconWrap} aria-hidden><PartyPopper size={22} /></div>
          <div className={styles.body}>
            <div className={styles.title}>{t('birthdayCard.replayTitle')}</div>
            <div className={styles.text}>{t('birthdayCard.replayLeft', { count: data.celebration.replays_remaining })}</div>
          </div>
          <button className={styles.claimBtn} disabled={replay.isPending} onClick={() => replay.mutate()}>
            {replay.isPending
              ? <><Loader2 size={14} className={styles.spin} /> {t('birthdayCard.replaying')}</>
              : t('birthdayCard.replay')}
          </button>
        </div>
      </div>
    );
  }

  if (withheld) {
    return (
      <div className={`${styles.card} ${styles.withheld}`}>
        <Cake size={20} className={styles.withheldIcon} />
        <span>{t('birthdayCard.withheld')}</span>
      </div>
    );
  }

  // Hide the claim button once a (non-withheld) claim has landed — the overlay
  // is up and the refetch will surface the replay card.
  const justClaimed = claim.isSuccess && !claim.data?.withheld;
  if (!data || !data.claim_eligible || justClaimed) return null;

  return (
    <div className={styles.card}>
      <div className={styles.row}>
        <div className={styles.iconWrap} aria-hidden><Cake size={22} /></div>
        <div className={styles.body}>
          <div className={styles.title}>{t('birthdayCard.title')}</div>
          <div className={styles.text}>{t('birthdayCard.body', { points: data.bonus_points })}</div>
        </div>
        <button className={styles.claimBtn} disabled={claim.isPending} onClick={() => claim.mutate()}>
          {claim.isPending
            ? <><Loader2 size={14} className={styles.spin} /> {t('birthdayCard.claiming')}</>
            : t('birthdayCard.claim')}
        </button>
      </div>
      {claim.isError && <div className={styles.err}>{t('birthdayCard.error')}</div>}
    </div>
  );
}
